import { DocumentTextIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { CheckBadgeIcon, EnvelopeOpenIcon } from '@heroicons/react/24/solid';
import { Badge as FlowbiteBadge, Button, Tooltip } from 'flowbite-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import type { Copro } from '../../../../../@types/Copro';
import type { Owner } from '../../../../../@types/Owner';
import { useReferentSendAvailability } from '../../../../../hooks/useReferentSendAvailability';
import type { RootState } from '../../../../../store/store';
import { JWTAuthorization } from '../../../../../utils/JWTHeader';
import Badge from '../../../../common/Badge';
import MUITable from '../../../../common/MUITable';
import { showToast } from '../../../../common/Notification';
import Popup from '../../../../common/Popup';
import Spinner from '../../../../common/Spinner';
import CoproLocalOwned from '../../../components/CoproLocalOwned';
import DpeDetailsPopup from '../../../components/DpeDetailsPopup';
import { formatDpeDetailsFromOwner } from '../../../utils/formatters';
import type { DpeGeneralMailTemplateKey } from './dpeGeneralMailContent';
import { DPE_GENERAL_MAIL_TEMPLATES, isDpeGeneralMailTemplateKey } from './dpeGeneralMailContent';
import DpeGeneralMailingPreview from './DpeGeneralMailingPreview';

interface DpeGeneralMailingSend {
  id: number;
  uuid: string;
  templateKey: DpeGeneralMailTemplateKey;
  sentAt: string;
  sentBy: { id: number; firstname?: string; lastname?: string; email?: string } | null;
  copro: Copro;
  triggeringDpe?: {
    id: number;
    remoteId?: string;
    details?: Record<string, unknown>[] | Record<string, unknown>;
    establishedAt?: string;
    receivedAt?: string;
    infoDPE?: { class?: string; [key: string]: unknown };
    createdAt?: string;
  } | null;
}

interface DpeGeneralMailingsTableProps {
  initialCoproId?: number | null;
}

const headers = [
  { title: 'Propriété' },
  { title: 'DPE déclencheur' },
  { title: 'Date de préparation' },
  { title: 'Modèle préparé' },
  { title: 'Préparé par' },
  { title: 'Prochaine relance possible' },
  {
    title: 'Actions',
    customContentClass: 'align-top py-2 leading-normal',
  },
];

const formatFrDate = (iso: string | null | undefined): string => {
  if (!iso) {
    return '—';
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return iso;
  }
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const buildReferentFullName = (
  brandingSettings:
    | undefined
    | null
    | {
        referents?: { firstname?: string; lastname?: string }[];
        commercialName?: string;
      },
): string => {
  const first = brandingSettings?.referents?.[0];
  if (first && (first.firstname || first.lastname)) {
    return `${first.firstname ?? ''} ${(first.lastname ?? '').toUpperCase()}`.trim();
  }
  return 'Référent Mutation Location';
};

function DpeGeneralMailingsTable({ initialCoproId = null }: DpeGeneralMailingsTableProps) {
  const company = useSelector((state: RootState) => state.auth.company);
  const jwt = useSelector((state: RootState) => state.auth.token);
  const { getOwnerCompany } = useReferentSendAvailability(company?.id);

  const [sends, setSends] = useState<DpeGeneralMailingSend[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeSend, setActiveSend] = useState<DpeGeneralMailingSend | null>(null);
  const [selectedTemplateKey, setSelectedTemplateKey] = useState<Record<number, DpeGeneralMailTemplateKey>>({});
  const [rdvUrls, setRdvUrls] = useState<Record<number, string>>({});
  const [confirmingId, setConfirmingId] = useState<number | null>(null);
  const [isDpeDetailsOpen, setIsDpeDetailsOpen] = useState(false);
  const [selectedDpeDetails, setSelectedDpeDetails] = useState<Record<string, unknown>>({});

  const handleDpeDetailClick = (details?: Record<string, unknown>) => {
    setSelectedDpeDetails(details ?? {});
    setIsDpeDetailsOpen(true);
  };

  const buildSyntheticOwnerForDpe = (send: DpeGeneralMailingSend): Owner | null => {
    if (!send.triggeringDpe) {
      return null;
    }
    return {
      lastname: '',
      civility: 0,
      doorNumber: '',
      step: 'not-contacted',
      dpe: send.triggeringDpe as unknown as Owner['dpe'],
    } as Owner;
  };

  const buildSyntheticOwnerForCopro = (send: DpeGeneralMailingSend): Owner => {
    // Synthetic Owner shape so CoproLocalOwned can render the name + full
    // address block identically to the other "Communication" tabs.
    return {
      lastname: '',
      civility: 0,
      doorNumber: '',
      step: 'not-contacted',
      dpe: send.triggeringDpe
        ? ({
          ...send.triggeringDpe,
          copro: send.copro,
        } as unknown as Owner['dpe'])
        : undefined,
      incomingCallCopro: send.copro,
    } as Owner;
  };

  const defaultTemplate: DpeGeneralMailTemplateKey = useMemo(() => {
    const bs = company?.brandingSettings as { defaultDpeGeneralMailTemplate?: string } | undefined;
    const v = bs?.defaultDpeGeneralMailTemplate;
    if (isDpeGeneralMailTemplateKey(v)) {
      return v;
    }
    return 'v1';
  }, [company]);

  // The mail carries the identity of the company holding the copro, not the subcontractor's.
  const mailCompany = activeSend ? getOwnerCompany(buildSyntheticOwnerForCopro(activeSend)) : company;
  const referentFullName = buildReferentFullName(mailCompany?.brandingSettings as Parameters<typeof buildReferentFullName>[0]);
  const syndicName = (mailCompany?.brandingSettings as { commercialName?: string } | undefined)?.commercialName ?? mailCompany?.name ?? '';

  const ensureRdvUrlLoaded = async(send: DpeGeneralMailingSend) => {
    if (rdvUrls[send.id]) {
      return;
    }
    if (!jwt) {
      return;
    }
    try {
      const response = await fetch(
        `/api/copros/${send.copro.id}/dpe-general-mailing/rdv-url`,
        {
          method: 'GET',
          headers: {
            Authorization: JWTAuthorization(jwt),
            'Content-Type': 'application/json',
          },
        },
      );
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const payload = (await response.json()) as { rdvUrl: string };
      setRdvUrls((prev) => ({ ...prev, [send.id]: payload.rdvUrl }));
    } catch (error) {
      console.error('Error fetching DPE general mailing rdv URL:', error);
    }
  };

  const openPreviewFor = (send: DpeGeneralMailingSend) => {
    setActiveSend(send);
    ensureRdvUrlLoaded(send);
  };

  const fetchSends = async() => {
    if (!company?.id || !jwt) {
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams({ step: '1', limit: '50' });
      if (initialCoproId) {
        params.set('filterCoproId', String(initialCoproId));
      }
      const response = await fetch(`/api/companies/${company.id}/dpe-general-mailing-sends?${params.toString()}`, {
        method: 'GET',
        headers: {
          Authorization: JWTAuthorization(jwt),
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const json = await response.json();
      const results: DpeGeneralMailingSend[] = json.results || [];
      setSends(results);
      const initialSelection: Record<number, DpeGeneralMailTemplateKey> = {};
      results.forEach((row) => {
        initialSelection[row.id] = row.templateKey ?? defaultTemplate;
      });
      setSelectedTemplateKey(initialSelection);
      if (initialCoproId && results.length > 0) {
        // When the user landed on this tab from DpeStep3, open the freshly
        // recorded send directly so they can preview / copy / DOCX-export it.
        openPreviewFor(results[0]);
      }
    } catch (error) {
      console.error('Error fetching DPE general mailing sends:', error);
      showToast('error', 'Erreur lors du chargement des envois généraux DPE.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSends();
     
  }, [company?.id, jwt, initialCoproId]);

  const handleConfirmTemplate = async(send: DpeGeneralMailingSend, key: DpeGeneralMailTemplateKey) => {
    if (!jwt) {
      return;
    }
    setConfirmingId(send.id);
    try {
      const response = await fetch(`/api/dpe-general-mailing-sends/${send.id}`, {
        method: 'PATCH',
        headers: {
          Authorization: JWTAuthorization(jwt),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ templateKey: key }),
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      setSends((prev) => prev.map((row) => (row.id === send.id ? { ...row, templateKey: key } : row)));
      showToast('success', 'Modèle confirmé pour cet envoi.');
    } catch (error) {
      console.error('Error confirming template:', error);
      showToast('error', 'Impossible d\'enregistrer le modèle pour cet envoi.');
    } finally {
      setConfirmingId(null);
    }
  };

  if (loading) {
    return (
      <p className="mx-10 my-32 text-center">
        <Spinner aria-label="Center-aligned spinner" size="xl" />
      </p>
    );
  }

  if (sends.length === 0) {
    return (
      <div className="p-4">
        <div className="mb-4 border-l-4 border-blue-500 bg-blue-50 p-4 text-sm text-blue-900">
          <p className="font-medium">Comment fonctionne cet onglet&nbsp;?</p>
          <p className="mt-1">
            Cet onglet liste les copropriétés pour lesquelles vous avez choisi, depuis la qualification d&apos;un DPE,
            de préparer un envoi général à l&apos;ensemble des copropriétaires.
          </p>
          <p className="mt-1">
            Le mail n&apos;est pas envoyé automatiquement&nbsp;: après préparation, vous copiez le corps du mail
            ou téléchargez le .docx généré pour le transmettre via la liste de diffusion du syndic.
          </p>
        </div>
        <div className="w-full text-center">
          <Badge mode="warning" text="Aucun envoi général DPE préparé pour le moment." className="mx-10 my-32" />
        </div>
      </div>
    );
  }

  const tableData = sends.map((row) => {
    const nextEligibleDate = new Date(row.sentAt);
    nextEligibleDate.setMonth(nextEligibleDate.getMonth() + 2);
    const now = new Date();
    const isLocked = nextEligibleDate > now;
    const effectiveTemplateKey = row.templateKey ?? defaultTemplate;
    const isUsingCompanyDefault = effectiveTemplateKey === defaultTemplate;
    const syntheticOwner = buildSyntheticOwnerForDpe(row);

    const propertyCell = (
      <CoproLocalOwned owner={buildSyntheticOwnerForCopro(row)} />
    );

    const dpeCell = syntheticOwner
      ? formatDpeDetailsFromOwner(syntheticOwner, handleDpeDetailClick)
      : <span className="text-gray-400">—</span>;

    const triggeredAtCell = (
      <Tooltip
        content="Date à laquelle un opérateur a choisi « Envoyer à tous les copropriétaires » lors de la qualification d'un DPE."
      >
        <span className="cursor-help underline decoration-gray-300 decoration-dotted">
          {formatFrDate(row.sentAt)}
        </span>
      </Tooltip>
    );

    const templateCell = (
      <div className="flex flex-col gap-1">
        <FlowbiteBadge color="purple">
          {DPE_GENERAL_MAIL_TEMPLATES[effectiveTemplateKey].label}
        </FlowbiteBadge>
        {isUsingCompanyDefault && (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold tracking-wide text-amber-700 uppercase">
            <CheckBadgeIcon className="h-3.5 w-3.5" />
            Modèle par défaut
          </span>
        )}
      </div>
    );

    const sentByCell = row.sentBy
      ? `${row.sentBy.firstname ?? ''} ${row.sentBy.lastname ?? ''}`.trim() || row.sentBy.email
      : '—';

    const nextEligibleCell = (
      <div className="flex flex-col gap-1">
        <span className="text-gray-800">{formatFrDate(nextEligibleDate.toISOString())}</span>
        {isLocked
          ? (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold tracking-wide text-amber-700 uppercase">
              <LockClosedIcon className="h-3.5 w-3.5" />
              Verrouillé 2 mois
            </span>
          )
          : (
            <span className="text-[10px] font-semibold tracking-wide text-green-700 uppercase">
              Nouvel envoi possible
            </span>
          )}
      </div>
    );

    const actionsCell = (
      <Button
        size="xs"
        color="purple"
        onClick={() => openPreviewFor(row)}
      >
        <DocumentTextIcon className="mr-1.5 h-4 w-4" />
        Préparer / consulter le mail
      </Button>
    );

    return {
      'Propriété': propertyCell,
      'DPE déclencheur': dpeCell,
      'Date de préparation': triggeredAtCell,
      'Modèle préparé': templateCell,
      'Préparé par': sentByCell,
      'Prochaine relance possible': nextEligibleCell,
      'Actions': actionsCell,
    };
  });

  return (
    <div className="p-4">
      <div className="mb-4 border-l-4 border-blue-500 bg-blue-50 p-4 text-sm text-blue-900">
        <p className="font-medium">
          <EnvelopeOpenIcon className="-mt-0.5 mr-1 inline h-4 w-4" />
          Comment fonctionne cet onglet&nbsp;?
        </p>
        <ul className="mt-2 ml-5 list-disc space-y-1">
          <li>
            Chaque ligne représente une copropriété pour laquelle vous avez préparé un envoi général
            depuis la qualification d&apos;un DPE.
          </li>
          <li>
            Le mail n&apos;est pas envoyé automatiquement&nbsp;: ouvrez l&apos;envoi pour copier le corps
            du mail ou télécharger le .docx, puis transmettez-le depuis la liste de diffusion du syndic.
          </li>
          <li>
            Pour éviter de sur-solliciter les copropriétaires, une copropriété ne peut être ciblée
            qu&apos;une fois tous les 2 mois.
          </li>
        </ul>
      </div>

      <MUITable
        data={tableData}
        headers={headers}
        disablePagination
      />

      <DpeDetailsPopup
        isOpen={isDpeDetailsOpen}
        onClose={() => setIsDpeDetailsOpen(false)}
        dpeDetails={selectedDpeDetails}
      />

      {activeSend && (
        <Popup
          isOpen={!!activeSend}
          onClose={() => setActiveSend(null)}
          title={`Préparer le mail — ${activeSend.copro?.name ?? `Copropriété #${activeSend.copro.id}`}`}
          width="90%"
          maxWidth="1100px"
          height="auto"
          maxHeight="90vh"
        >
          <DpeGeneralMailingPreview
            coproName={activeSend.copro?.name}
            rdvUrl={rdvUrls[activeSend.id] ?? ''}
            referentFullName={referentFullName}
            syndicName={syndicName}
            selectedTemplateKey={selectedTemplateKey[activeSend.id] ?? activeSend.templateKey ?? defaultTemplate}
            defaultTemplateKey={defaultTemplate}
            onTemplateKeyChange={(key) => {
              setSelectedTemplateKey((prev) => ({ ...prev, [activeSend.id]: key }));
            }}
            onConfirm={(key) => handleConfirmTemplate(activeSend, key)}
            confirming={confirmingId === activeSend.id}
          />
        </Popup>
      )}
    </div>
  );
}

export default DpeGeneralMailingsTable;
